// platform/frontend/src/components/plans/CreatePlanForm.jsx
import React, { useState, useEffect, useContext, useRef } from "react";
import SignatureField from "../SignatureField";
import { useAuditedPlanOperations } from "../../hooks/useAuditedPlanOperations";

import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import { getPreviewUrlFromPath } from "../../utils/fileHelpers";

const today = new Date().toISOString().split("T")[0];

const initialFormState = {
  consumer_id: "",
  planType: "initial",
  plan_name: "",
  participants: "",
  strengths_and_resources: "",
  issues_identified: "",
  goals_and_objectives: "",
  timeframe: "",
  frequency: "",
  responsible_person: "",
  approval_status: "",
  status_id: "",
  date_of_plan: today,
  cultural: "",
  coordination: "",
  consumer_signature_date: today,
  crp_signature_date: today,
  wvdrs_signature_date: today,
};

const CreatePlanForm = ({ onCreated }) => {
  const { tenant_id, user } = useContext(AuthContext);
  const created_by = user.user_id;
  const apiBase = getApiBaseURL();
  const { auditPlanCreate, auditPlanExport } = useAuditedPlanOperations();

  const [formData, setFormData] = useState(initialFormState);

  const [consumerSignature, setConsumerSignature] = useState("");
  const [crpSignature, setCrpSignature] = useState("");
  const [wvdrsSignature, setWvdrsSignature] = useState("");

  const [consumers, setConsumers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [message, setMessage] = useState("");

  const consumerSigRef = useRef(null);
  const crpSigRef = useRef(null);
  const wvdrsSigRef = useRef(null);

  useEffect(() => {
    if (!tenant_id || !user?.user_id || !user?.role || !user?.tenant_type)
      return;

    secureAxios
      .get(`${apiBase}/api/consumers`, {
        params: {
          tenant_id,
          user_id: user.user_id,
          role: user.role,
          tenant_type: user.tenant_type,
        },
      })
      .then((res) => {
        // Filter to only include active consumers
        // Note: 'status' field comes from LEFT JOIN with status table in the API
        const activeConsumers = res.data.filter(
          (consumer) =>
            consumer.status && consumer.status.toLowerCase() === "active"
        );
        setConsumers(activeConsumers);
      })
      .catch((err) => {
        setMessage("Failed to load consumers. Please refresh the page.");
      });
  }, [tenant_id, user, apiBase]);

  // This works around the 404 issue
  useEffect(() => {
    const selectedConsumer = consumers.find(
      (c) => c.consumer_id === formData.consumer_id
    );
    if (!selectedConsumer || !selectedConsumer.counselor_id) return;

    // CHANGED: Get services through the all counselors endpoint instead
    secureAxios
      .get(`${apiBase}/api/counselors/all`, {
        params: { tenant_id },
      })
      .then((res) => {
        // Find the specific counselor from the list
        const counselor = res.data.find(
          (c) => c.counselor_id === selectedConsumer.counselor_id
        );
        if (!counselor || !counselor.customer_id) {
          throw new Error("Counselor or customer_id not found");
        }

        // Now fetch services using the customer_id
        return secureAxios.get(
          `${apiBase}/api/services?tenant_id=${tenant_id}&customer_id=${counselor.customer_id}`
        );
      })
      .then((res) => setAllServices(res.data))
      .catch((err) => {
        console.error("Error loading services:", err);
        // ADDED: Fallback approach if the above fails
        secureAxios
          .get(`${apiBase}/api/services?tenant_id=${tenant_id}`)
          .then((res) => setAllServices(res.data))
          .catch((fallbackErr) => {
            console.error("Fallback service loading also failed:", fallbackErr);
            setMessage("Failed to load services. Please refresh the page.");
          });
      });
  }, [formData.consumer_id, consumers, tenant_id, apiBase]);

  useEffect(() => {
    secureAxios
      .get(`${apiBase}/api/status`)
      .then((res) => setStatuses(res.data))
      .catch((err) => {
        // Log error silently in production
        setMessage("Failed to load statuses. Please refresh the page.");
      });
  }, [apiBase]);

  useEffect(() => {
    if (statuses.length > 0 && !formData.status_id) {
      const pending = statuses.find(
        (s) => s.status.toLowerCase() === "pending"
      );
      if (pending)
        setFormData((prev) => ({ ...prev, status_id: pending.status_id }));
    }
  }, [statuses, formData.status_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceCheckbox = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const truncate = (val, limit) =>
    val && val.length > limit ? val.substring(0, limit) : val;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("Submitting...");

    // Manually capture signatures from refs
    let finalConsumerSig = consumerSignature;
    let finalCrpSig = crpSignature;
    let finalWvdrsSig = wvdrsSignature;

    // Try to get current signature from ref if state is empty
    if (!finalConsumerSig && consumerSigRef.current) {
      finalConsumerSig = consumerSigRef.current.getCurrentSignature() || "";
    }
    if (!finalCrpSig && crpSigRef.current) {
      finalCrpSig = crpSigRef.current.getCurrentSignature() || "";
    }
    if (!finalWvdrsSig && wvdrsSigRef.current) {
      finalWvdrsSig = wvdrsSigRef.current.getCurrentSignature() || "";
    }

    const payload = {
      ...formData,
      plan_name: truncate(formData.plan_name, 100),
      timeframe: formData.timeframe,
      frequency: formData.frequency,
      responsible_person: formData.responsible_person,
      approval_status: formData.approval_status,
      initial_plan: formData.planType === "initial",
      updated_plan: formData.planType === "updated",
      tenant_id,
      created_by,
      date_of_plan: formData.date_of_plan?.trim() || null,
      consumer_signature_date: formData.consumer_signature_date?.trim() || null,
      crp_signature_date: formData.crp_signature_date?.trim() || null,
      wvdrs_signature_date: formData.wvdrs_signature_date?.trim() || null,
      consumer_signature: finalConsumerSig,
      crp_signature: finalCrpSig,
      wvdrs_signature: finalWvdrsSig,
    };

    try {
      const response = await secureAxios.post(`${apiBase}/api/plans`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      const plan_id = response.data.plan_id;

      // Audit the plan creation
      await auditPlanCreate(payload, plan_id);

      if (!plan_id) return;

      if (selectedServices.length > 0) {
        await secureAxios.post(`${apiBase}/api/plans/assign-services`, {
          plan_id,
          tenant_id,
          service_ids: selectedServices,
        });
      }

      const pdfRes = await secureAxios.get(
        `${apiBase}/api/plans/${plan_id}/pdf?tenant_id=${tenant_id}&isBackground=true`
      );

      if (pdfRes?.data?.filename) {
        // Audit the PDF export
        await auditPlanExport({ plan_id, plan_name: formData.plan_name });

        // Open PDF in new tab
        const url = `${apiBase}/api/documents/preview/filename/${encodeURIComponent(
          pdfRes.data.filename
        )}`;
        window.open(url, "_blank");
      }
    } catch (err) {
      // Log error silently in production
      setMessage("Failed to create plan. Please try again.");
    } finally {
      if (onCreated) onCreated();
      setTimeout(() => {
        setMessage("");
        setFormData(initialFormState);
        setConsumerSignature("");
        setCrpSignature("");
        setWvdrsSignature("");
        window.scrollTo(0, 0);
      }, 2000);
    }
  };

  return (
    <div>
      <h2>Create New Plan</h2>

      {/* HIPAA Compliance Notice */}
      <div className="alert alert-info mb-4">
        <h6 className="alert-heading">
          <i className="bi bi-shield-lock-fill me-2"></i>
          Protected Health Information (PHI) Notice
        </h6>
        <p className="mb-2">
          This form contains Protected Health Information (PHI) under HIPAA
          regulations. By accessing this form, you acknowledge that:
        </p>
        <ul className="mb-0">
          <li>
            You are authorized to access this consumer's health information
          </li>
          <li>You will use this information only for authorized purposes</li>
          <li>All actions are being recorded in an audit log for compliance</li>
          <li>
            Unauthorized access or disclosure may result in civil and criminal
            penalties
          </li>
        </ul>
      </div>

      {message && <p className="fw-bold">{message}</p>}
      <form onSubmit={handleSubmit} className="row g-3">
        {/* Consumer and Plan Info */}
        <div className="col-md-12">
          <label className="form-label">Plan Name</label>
          <input
            className="form-control"
            name="plan_name"
            value={formData.plan_name || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Consumer</label>
          <select
            name="consumer_id"
            value={formData.consumer_id}
            onChange={handleChange}
            className="form-select"
            required
          >
            <option value="">Select Consumer</option>
            {consumers.map((c) => (
              <option key={c.consumer_id} value={c.consumer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">Date of Plan:</label>
          <input
            type="date"
            name="date_of_plan"
            value={formData.date_of_plan}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">Services</label>
          <div className="row">
            {allServices.map((service) => (
              <div className="col-md-4" key={service.service_id}>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`s-${service.service_id}`}
                    checked={selectedServices.includes(service.service_id)}
                    onChange={() => handleServiceCheckbox(service.service_id)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`s-${service.service_id}`}
                  >
                    {service.service_name}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Type */}
        <div className="col-md-12 d-flex gap-1 align-items-center">
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="planType"
              value="initial"
              checked={formData.planType === "initial"}
              onChange={handleChange}
            />
            <label className="form-check-label">Initial Plan</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="planType"
              value="updated"
              checked={formData.planType === "updated"}
              onChange={handleChange}
            />
            <label className="form-check-label">Updated Plan</label>
          </div>
        </div>

        <div className="col-md-12">
          <label className="form-label">
            Individuals involved in the development of plan: Name
            (Consumer/Agency/Title/Family Member/Other)
          </label>
          <textarea
            name="participants"
            value={formData.participants}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">Strengths and resources:</label>
          <textarea
            name="strengths_and_resources"
            value={formData.strengths_and_resources}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">Issues/needs identified:</label>
          <textarea
            name="issues_identified"
            value={formData.issues_identified}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">
            Cultural considerations, resources and needs:
          </label>
          <textarea
            name="cultural"
            value={formData.cultural}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">
            Coordination of Services - (identify other services consumer is
            receiving and explain how they are being coordinated):
          </label>
          <textarea
            name="coordination"
            value={formData.coordination}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        {/* Digital Signature Section with HIPAA Notice */}
        <div className="col-12">
          <div className="alert alert-warning">
            <h6 className="alert-heading">
              <i className="bi bi-pen me-2"></i>
              Digital Signature Requirements
            </h6>
            <p className="mb-0">
              The signatures below serve as legal authorization for this plan.
              By signing, each party acknowledges they have reviewed and agree
              to the plan contents. Digital signatures are legally binding and
              HIPAA-compliant.
            </p>
          </div>
        </div>

        <div className="col-md-12">
          <label className="form-label">Consumer Signature</label>
          <div style={{ border: "1px solid #ccc", padding: "10px" }}>
            <SignatureField
              ref={consumerSigRef}
              value={consumerSignature}
              onChange={setConsumerSignature}
              penColor="blue"
              label=""
            />
          </div>
          <div className="col-md-6 mt-2">
            <label className="form-label">Consumer Signature Date</label>
            <input
              type="date"
              className="form-control"
              name="consumer_signature_date"
              value={formData.consumer_signature_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-md-12">
          <label className="form-label">CRP Signature</label>
          <div style={{ border: "1px solid #ccc", padding: "10px" }}>
            <SignatureField
              ref={crpSigRef}
              value={crpSignature}
              onChange={setCrpSignature}
              penColor="blue"
              label=""
            />
          </div>
          <div className="col-md-6 mt-2">
            <label className="form-label">CRP Signature Date</label>
            <input
              type="date"
              className="form-control"
              name="crp_signature_date"
              value={formData.crp_signature_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="col-md-12">
          <label className="form-label">WVDRS Signature</label>
          <div style={{ border: "1px solid #ccc", padding: "10px" }}>
            <SignatureField
              ref={wvdrsSigRef}
              value={wvdrsSignature}
              onChange={setWvdrsSignature}
              penColor="blue"
              label=""
            />
          </div>
          <div className="col-md-6 mt-2">
            <label className="form-label">WVDRS Signature Date</label>
            <input
              type="date"
              className="form-control"
              name="wvdrs_signature_date"
              value={formData.wvdrs_signature_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {[
          "goals_and_objectives",
          "timeframe",
          "frequency",
          "responsible_person",
        ].map((key) => (
          <div className="col-md-6" key={key}>
            <label className="form-label">
              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            </label>
            <textarea
              className="form-control"
              name={key}
              value={formData[key]}
              onChange={handleChange}
            />
          </div>
        ))}

        <div className="col-12">
          <button type="submit" className="btn btn-primary">
            Create Plan
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePlanForm;